import { useTranslations } from 'use-intl';
import { useReportNarrative } from '@/app/queries';
import { AiAnswerFrame, AiRefusal } from '@/ui/AiAnswer';
import { AiButton } from '@/ui/AiButton';
import { Notice } from '@/ui/Panel';

/**
 * The words that go beside a report's figures, for whoever is not a coach.
 *
 * It is given the same numbers the page prints and told it may not use any
 * other, so the narrative and the table cannot disagree. The caveats it returns
 * are the part worth reading before handing the page to a board: a short period
 * or a thin squad says so there rather than being quietly rounded away.
 */
export function AiNarrative({
  teamId,
  kind,
  from,
  to,
  figures,
}: {
  teamId: string;
  kind: string;
  from: string;
  to: string;
  figures: Record<string, string | number | null>;
}) {
  const t = useTranslations();
  const narrative = useReportNarrative();
  const answer = narrative.data;
  const refusal = narrative.error as { code?: string } | null;

  return (
    <div className="grid gap-3">
      <AiButton
        label={t('ai.writeNarrative')}
        busy={narrative.isPending}
        onRun={() => narrative.mutate({ teamId, kind, from, to, figures })}
      />
      {narrative.isPending ? <Notice>{t('ai.thinkingNarrative')}</Notice> : null}
      {refusal?.code ? <AiRefusal code={refusal.code} /> : null}

      {answer ? (
        <div className="overflow-hidden rounded-(--radius-panel) border border-line bg-surface">
          <AiAnswerFrame answer={answer}>
            <div className="grid gap-3">
              {answer.result.paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className="max-w-[70ch] leading-relaxed">
                  {p}
                </p>
              ))}
              {answer.result.caveats.length > 0 ? (
                <section>
                  <h4 className="text-meta font-semibold text-ink-muted">{t('ai.caveats')}</h4>
                  <ul className="mt-1 grid gap-0.5 text-meta text-ink-muted">
                    {answer.result.caveats.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          </AiAnswerFrame>
        </div>
      ) : null}
    </div>
  );
}
